// src/pages/admin/AdminDashboardPage.tsx
// admin.readlinker.com のトップ画面。NDL書誌・典拠の取り込みバッチ
// （NDL側が概ね週に一度、不定期にデータをアップロードし、それをS3イベント
// トリガーで検知して自動取り込みする仕組み。年末年始等は間が空くこともある）の
// 冊数・典拠数、および実行日ごとの結果を表示する。
// 現在総数はライブでCOUNT(*)するのではなく、バッチワーカーが都度更新する
// DynamoDBのカウンタ(BatchStatsTable)を読むだけなので高速
// （ndl_bibsは682万件超あり、素のCOUNT(*)は約24秒かかることを実測済み）。

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuth } from '../../context/AuthContext';
import { useAppTokens } from '../../context/ThemeModeContext';
import { NEW_API } from '../../config/api';
import AdminNav from './AdminNav';

interface BatchStat {
  total_count: number;
  last_run_delta: number;
  last_run_at: string | null;
  updated_at: string | null;
}

type BatchStatsResponse = Record<string, BatchStat>;

interface DailyStatRow {
  run_date: string;
  ndl_bibs_new_count: number | null;
  ndl_bibs_run_count: number | null;
  ndl_authorities_new_count: number | null;
  ndl_authorities_run_count: number | null;
  last_run_at: string | null;
}

const STAT_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  ndl_bibs: { label: '書誌（冊数）', icon: <MenuBookIcon /> },
  ndl_authorities: { label: '典拠（典拠数）', icon: <BadgeIcon /> },
};

const AdminDashboardPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [stats, setStats] = useState<BatchStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dailyRows, setDailyRows] = useState<DailyStatRow[] | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState<string | null>(null);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const fetchStats = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'get_batch_stats' },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setStats(res.data?.data ?? {});
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || '統計の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  const fetchDailyStats = useCallback(async () => {
    if (!idToken) return;
    setDailyLoading(true);
    setDailyError(null);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'get_batch_daily_stats', days: 30 },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setDailyRows(res.data?.data ?? []);
    } catch (e: any) {
      setDailyError(e?.response?.data?.error?.message || '実行日ごとの結果の取得に失敗しました');
    } finally {
      setDailyLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchStats();
    fetchDailyStats();
  }, [fetchStats, fetchDailyStats]);

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('ja-JP');
    } catch {
      return iso;
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', background: TOKENS.bg, color: TOKENS.text }}>
      <AdminNav />
      <Box sx={{ maxWidth: 900, margin: 'auto', px: 2, py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>ダッシュボード</Typography>
          <Button
            onClick={() => {
              fetchStats();
              fetchDailyStats();
            }}
            disabled={loading || dailyLoading}
            startIcon={loading || dailyLoading ? <CircularProgress size={14} sx={{ color: TOKENS.textSub }} /> : <RefreshIcon />}
            sx={{ textTransform: 'none', color: TOKENS.textSub }}
          >
            更新
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 2 }}>
          NDL書誌・典拠の取り込みバッチ（NDL側が概ね週に一度、不定期にアップロードする
          データをS3イベント経由で自動取り込みする仕組み）の現在の冊数・典拠数です。
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(STAT_LABELS).map(([key, meta]) => {
            const stat = stats?.[key];
            return (
              <Box key={key} sx={{ ...cardSx, flex: '1 1 280px', minWidth: 260 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: TOKENS.textSub }}>
                  {meta.icon}
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</Typography>
                </Box>
                {loading && !stats ? (
                  <CircularProgress size={20} sx={{ color: TOKENS.text }} />
                ) : (
                  <>
                    <Typography sx={{ fontSize: 32, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {stat ? stat.total_count.toLocaleString('ja-JP') : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.5 }}>
                      前回実行分: {stat && stat.last_run_delta > 0 ? `+${stat.last_run_delta.toLocaleString('ja-JP')}` : '±0'}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                      最終更新: {formatDateTime(stat?.last_run_at ?? null)}
                    </Typography>
                  </>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>実行日ごとの結果</Typography>
          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 2 }}>
            バッチが実際に実行された日だけが表示されます（過去分は遡って記録していないため、
            この画面をリリースした日以降のみ蓄積されます）。
          </Typography>

          {dailyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dailyError}
            </Alert>
          )}

          {dailyLoading && !dailyRows ? (
            <CircularProgress size={20} sx={{ color: TOKENS.text }} />
          ) : !dailyRows || dailyRows.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
              まだ記録がありません。次にバッチが実行されるとここに表示されます。
            </Typography>
          ) : (
            <TableContainer sx={{ ...cardSx, p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>日付</TableCell>
                    <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }} align="right">
                      書誌（新規）
                    </TableCell>
                    <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }} align="right">
                      典拠（新規）
                    </TableCell>
                    <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>
                      最終実行時刻
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyRows.map((row) => (
                    <TableRow key={row.run_date}>
                      <TableCell sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border }}>
                        {row.run_date}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {row.ndl_bibs_new_count != null
                          ? `+${row.ndl_bibs_new_count.toLocaleString('ja-JP')}${
                              row.ndl_bibs_run_count && row.ndl_bibs_run_count > 1 ? `（${row.ndl_bibs_run_count}回）` : ''
                            }`
                          : '—'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {row.ndl_authorities_new_count != null
                          ? `+${row.ndl_authorities_new_count.toLocaleString('ja-JP')}${
                              row.ndl_authorities_run_count && row.ndl_authorities_run_count > 1
                                ? `（${row.ndl_authorities_run_count}回）`
                                : ''
                            }`
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 11.5, borderColor: TOKENS.border }}>
                        {formatDateTime(row.last_run_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button component={Link} to="/admin/announcement" sx={{ textTransform: 'none', color: TOKENS.text, mr: 2 }}>
            お知らせ管理 →
          </Button>
          <Button component={Link} to="/admin/cache" sx={{ textTransform: 'none', color: TOKENS.text }}>
            書影キャッシュ再取得 →
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboardPage;
